import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import {
  cancelCorrectionRequest,
  fetchCorrectionRequests,
  reviewCorrectionRequest,
} from "../../../api/TimeTracking/timeTrackingApi";
import {
  ttKpiChipSx,
  ttResponsiveTableSx,
  ttTableContainerSx,
  ttToolbarButtonSx,
  ttToolbarSx,
} from "./timeTrackingStyles";
import {
  MobileActionGrid,
  MobileCardField,
  MobileEmptyState,
  MobileRecordCard,
  statusAccentFromMuiColor,
} from "./TimeTrackingMobileCards";
import TabContentShell from "./TabContentShell";
import CorrectionRequestDialog from "./CorrectionRequestDialog";
import CorrectionRequestsChart from "./CorrectionRequestsChart";
import { eventLabel } from "./clockEventLabels";

const STATUS_LABEL = {
  pending: "Pendiente",
  approved: "Aprobada",
  rejected: "Rechazada",
  cancelled: "Cancelada",
};

const STATUS_COLOR = {
  pending: "warning",
  approved: "success",
  rejected: "error",
  cancelled: "default",
};

const REQUEST_TYPE_LABEL = {
  correct_entry: "Corregir fichaje",
  add_missing: "Fichaje olvidado",
};

function formatDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-ES", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function formatRequestedClocking(row) {
  if (row.requestedExitRecordedAt) {
    return `Entrada ${formatDateTime(row.requestedRecordedAt)} · Salida ${formatDateTime(row.requestedExitRecordedAt)}`;
  }
  return `${eventLabel(row.requestedEventType)} · ${formatDateTime(row.requestedRecordedAt)}`;
}

function formatResultEntries(row) {
  if (row.resultExitEntryId) {
    return `Fichajes #${row.resultEntryId} y #${row.resultExitEntryId}`;
  }
  if (row.resultEntryId) {
    return `Fichaje #${row.resultEntryId}`;
  }
  return null;
}

const CorrectionRequestsTab = ({ selectedUserId, canRrhh, canFichaje }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("add");
  const [chartYear, setChartYear] = useState(() => new Date().getFullYear());

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { limit: 300 };
      if (selectedUserId) params.userId = selectedUserId;
      const data = await fetchCorrectionRequests(params);
      setRows(data);
    } catch (e) {
      setError(e?.response?.data?.error || "Error cargando solicitudes");
    } finally {
      setLoading(false);
    }
  }, [selectedUserId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleReview = async (id, status) => {
    try {
      setError(null);
      await reviewCorrectionRequest(id, { status });
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || "Error revisando solicitud");
    }
  };

  const handleCancel = async (id) => {
    try {
      setError(null);
      await cancelCorrectionRequest(id);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || "Error cancelando solicitud");
    }
  };

  const openAdd = () => {
    setDialogMode("add");
    setDialogOpen(true);
  };

  const showEmployeeCol =
    selectedUserId == null && canRrhh && rows.some((r) => r.employee);

  const pendingCount = rows.filter((r) => r.status === "pending").length;

  return (
    <Box>
      <TabContentShell accentColor={theme.palette.secondary.main}>
        {error ? (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        ) : null}

        <Box sx={ttToolbarSx(theme)}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshOutlinedIcon />}
            onClick={load}
            disabled={loading}
            sx={ttToolbarButtonSx}
          >
            Actualizar
          </Button>
          <Box sx={{ flex: 1, display: { xs: "none", sm: "block" } }} />
          {canFichaje ? (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddOutlinedIcon />}
              onClick={openAdd}
              sx={{ ...ttToolbarButtonSx, width: { xs: "100%", sm: "auto" } }}
            >
              Solicitar fichaje olvidado
            </Button>
          ) : null}
        </Box>

        {canRrhh && (!loading || rows.length > 0) ? (
          <CorrectionRequestsChart
            rows={rows}
            year={chartYear}
            onYearChange={setChartYear}
          />
        ) : null}

        {canRrhh ? (
          <Alert
            severity="info"
            sx={{
              mb: 1.5,
              borderRadius: 2,
              display: { xs: "none", md: "flex" },
            }}
          >
            Revisa las solicitudes de los empleados. Al aprobar, se aplicará la
            corrección en el historial (nueva fila, sin borrar el original).
          </Alert>
        ) : (
          <Alert
            severity="info"
            sx={{
              mb: 1.5,
              borderRadius: 2,
              display: { xs: "none", md: "flex" },
            }}
          >
            Puedes solicitar correcciones o fichajes olvidados. RRHH debe
            aprobarlas antes de que se reflejen en tu historial.
          </Alert>
        )}

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ mb: 1.25 }}
        >
          <Chip
            label={`${rows.length} solicitud(es)`}
            size="small"
            sx={ttKpiChipSx}
          />
          {canRrhh && pendingCount > 0 ? (
            <Chip
              label={`${pendingCount} pendiente(s)`}
              size="small"
              color="warning"
              sx={ttKpiChipSx}
            />
          ) : null}
        </Stack>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : isMobile ? (
          <Stack spacing={1.5}>
            {rows.map((r) => {
              const status = STATUS_COLOR[r.status] || "default";
              const actions = [];
              if (r.status === "pending" && canRrhh) {
                actions.push(
                  {
                    key: "approve",
                    label: "Aprobar",
                    color: "success",
                    onClick: () => handleReview(r.id, "approved"),
                  },
                  {
                    key: "reject",
                    label: "Rechazar",
                    color: "error",
                    onClick: () => handleReview(r.id, "rejected"),
                  },
                );
              } else if (r.status === "pending" && !canRrhh && canFichaje) {
                actions.push({
                  key: "cancel",
                  label: "Cancelar",
                  onClick: () => handleCancel(r.id),
                });
              }

              return (
                <MobileRecordCard
                  key={r.id}
                  theme={theme}
                  accent={statusAccentFromMuiColor(theme, status)}
                  avatarName={
                    showEmployeeCol
                      ? r.employee?.username || String(r.userId)
                      : null
                  }
                  title={
                    showEmployeeCol
                      ? r.employee?.username || String(r.userId)
                      : REQUEST_TYPE_LABEL[r.requestType] || r.requestType
                  }
                  subtitle={
                    showEmployeeCol
                      ? REQUEST_TYPE_LABEL[r.requestType] || r.requestType
                      : formatDateTime(r.requestedAt)
                  }
                  statusLabel={STATUS_LABEL[r.status] || r.status}
                  statusColor={status}
                  footer={
                    actions.length ? (
                      <MobileActionGrid theme={theme} actions={actions} />
                    ) : r.status === "approved" && formatResultEntries(r) ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        align="center"
                        display="block"
                      >
                        {formatResultEntries(r)}
                      </Typography>
                    ) : null
                  }
                >
                  <Stack spacing={1}>
                    <MobileCardField label="Fichaje solicitado">
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {formatRequestedClocking(r)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {r.requestedWorkMode}
                        {r.targetEntryId
                          ? ` · Fichaje #${r.targetEntryId}`
                          : ""}
                      </Typography>
                    </MobileCardField>
                    {r.reason ? (
                      <MobileCardField label="Motivo">
                        <Typography variant="body2">{r.reason}</Typography>
                        {r.reviewNotes ? (
                          <Typography variant="caption" color="text.secondary">
                            RRHH: {r.reviewNotes}
                          </Typography>
                        ) : null}
                      </MobileCardField>
                    ) : null}
                    {showEmployeeCol ? (
                      <Typography variant="caption" color="text.secondary">
                        Solicitada: {formatDateTime(r.requestedAt)}
                      </Typography>
                    ) : null}
                  </Stack>
                </MobileRecordCard>
              );
            })}
            {!rows.length ? (
              <MobileEmptyState>Sin solicitudes de corrección</MobileEmptyState>
            ) : null}
          </Stack>
        ) : (
          <TableContainer sx={ttTableContainerSx(theme)}>
            <Table size="small" sx={ttResponsiveTableSx}>
              <TableHead>
                <TableRow>
                  {showEmployeeCol ? <TableCell>Empleado</TableCell> : null}
                  <TableCell>Tipo</TableCell>
                  <TableCell>Fichaje solicitado</TableCell>
                  <TableCell>Motivo</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Solicitada</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} hover>
                    {showEmployeeCol ? (
                      <TableCell sx={{ fontWeight: 600 }}>
                        {r.employee?.username || r.userId}
                      </TableCell>
                    ) : null}
                    <TableCell>
                      <Chip
                        label={
                          REQUEST_TYPE_LABEL[r.requestType] || r.requestType
                        }
                        size="small"
                        variant="outlined"
                        sx={{
                          height: 22,
                          fontWeight: 700,
                          fontSize: "0.68rem",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatRequestedClocking(r)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {r.requestedWorkMode}
                        {r.targetEntryId
                          ? ` · Fichaje #${r.targetEntryId}`
                          : ""}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>
                      <Typography variant="body2" noWrap title={r.reason}>
                        {r.reason}
                      </Typography>
                      {r.reviewNotes ? (
                        <Typography variant="caption" color="text.secondary">
                          RRHH: {r.reviewNotes}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={STATUS_LABEL[r.status] || r.status}
                        size="small"
                        color={STATUS_COLOR[r.status] || "default"}
                        variant="outlined"
                        sx={{
                          height: 22,
                          fontWeight: 700,
                          fontSize: "0.68rem",
                        }}
                      />
                    </TableCell>
                    <TableCell>{formatDateTime(r.requestedAt)}</TableCell>
                    <TableCell align="right">
                      {r.status === "pending" && canRrhh ? (
                        <Stack
                          direction="row"
                          spacing={0.5}
                          justifyContent="flex-end"
                        >
                          <Button
                            size="small"
                            color="success"
                            onClick={() => handleReview(r.id, "approved")}
                          >
                            Aprobar
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            onClick={() => handleReview(r.id, "rejected")}
                          >
                            Rechazar
                          </Button>
                        </Stack>
                      ) : null}
                      {r.status === "pending" && !canRrhh && canFichaje ? (
                        <Button size="small" onClick={() => handleCancel(r.id)}>
                          Cancelar
                        </Button>
                      ) : null}
                      {r.status === "approved" && formatResultEntries(r) ? (
                        <Typography variant="caption" color="text.secondary">
                          {formatResultEntries(r)}
                        </Typography>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
                {!rows.length ? (
                  <TableRow>
                    <TableCell
                      colSpan={(showEmployeeCol ? 1 : 0) + 6}
                      align="center"
                      sx={{ py: 4 }}
                    >
                      <Typography color="text.secondary">
                        Sin solicitudes de corrección
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabContentShell>

      <CorrectionRequestDialog
        open={dialogOpen}
        mode={dialogMode}
        entry={null}
        onClose={() => setDialogOpen(false)}
        onSaved={load}
      />
    </Box>
  );
};

export default CorrectionRequestsTab;
